import { useEffect, useId, useRef, useState } from "react";
import { podcastAppLinks } from "../../../../shared/src/podcast-paths";
import { Menu } from "../ui-kit/menu";
import "./podcast-follow.css";

type Show = {
  creatorId: string;
  title: string;
  feedUrl: string;
  appleUrl: string;
  spotifyUrl: string;
};
type MemberFeed = { available: boolean; url: string | null };

async function copy(value: string, done: (message: string) => void) {
  try {
    await navigator.clipboard.writeText(value);
    done("Link copied. Paste it into your podcast app’s “Add by URL”.");
  } catch {
    done(`Copy this link into your podcast app: ${value}`);
  }
}

/** The apps that take a feed by its address, and a copy of the address for every other one. */
function appItems(feedUrl: string, copyLabel: string, apple: boolean) {
  return [
    ...podcastAppLinks(feedUrl)
      .filter((link) => apple || link.app !== "Apple Podcasts")
      .map((link) => ({ label: link.app, href: link.href })),
    { separator: true as const },
    { label: copyLabel, value: "copy" },
  ];
}

/**
 * A page's Podcast element: where to follow the show, and for a signed-in member their own
 * private feed. The member's link is read in the browser only, never in the page's HTML.
 */
export function PodcastFollow({
  show,
  title,
  signedIn,
  attributes = {},
  className = "drate-page-node",
  quiet = false,
}: {
  show: Show;
  title: string;
  signedIn: boolean;
  attributes?: Record<string, unknown>;
  className?: string;
  /** Beside other content (an episode's page): outlined buttons that don't lead the page. */
  quiet?: boolean;
}) {
  const lead = quiet ? "button button-outline" : "button button-primary";
  const id = useId().replace(/:/g, "");
  const root = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState("");
  const [member, setMember] = useState<MemberFeed | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    const controller = new AbortController();
    fetch(`/api/podcasts/${encodeURIComponent(show.creatorId)}/member`, {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then((response) => (response.ok ? (response.json() as Promise<MemberFeed>) : null))
      .then((feed) => setMember(feed))
      .catch(() => undefined);
    return () => controller.abort();
  }, [signedIn, show.creatorId]);

  useEffect(() => {
    const element = root.current;
    if (!element) return;
    const select = (event: Event) => {
      const target = (event.target as HTMLElement).closest("[data-podcast-menu]");
      const value = (event as CustomEvent<{ value: string }>).detail.value;
      if (value !== "copy") return;
      void copy(
        target?.getAttribute("data-podcast-menu") === "member" && member?.url
          ? member.url
          : show.feedUrl,
        setStatus,
      );
    };
    element.addEventListener("ui-menu-select", select);
    return () => element.removeEventListener("ui-menu-select", select);
  }, [member, show.feedUrl]);

  return (
    <div {...attributes} ref={root} className={`${className} podcast-follow`}>
      {title && <h3 className="podcast-follow-title">{title}</h3>}
      <div className="podcast-follow-row">
        {/* Directory pages open anywhere; the app links below need the app installed. */}
        {show.appleUrl && (
          <a className={lead} href={show.appleUrl} target="_blank" rel="noopener noreferrer">
            Apple Podcasts
          </a>
        )}
        {show.spotifyUrl && (
          <a
            className={show.appleUrl ? "button button-outline" : lead}
            href={show.spotifyUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Spotify
          </a>
        )}
        <div data-podcast-menu="public">
          <Menu
            id={`podcast-${id}-apps`}
            label={
              show.appleUrl || show.spotifyUrl
                ? "Other apps"
                : quiet
                  ? "Listen in a podcast app"
                  : "Follow the podcast"
            }
            triggerClass={show.appleUrl || show.spotifyUrl ? "button button-outline" : lead}
            items={appItems(show.feedUrl, "Copy RSS link", !show.appleUrl)}
          />
        </div>
      </div>
      {member?.available && (
        <div className="podcast-follow-member">
          <p className="podcast-follow-note">
            Your private feed has every episode your membership includes.
          </p>
          {member.url ? (
            <div className="podcast-follow-row">
              <a className="button button-outline" href={podcastAppLinks(member.url)[0].href}>
                Add to Apple Podcasts
              </a>
              <div data-podcast-menu="member">
                <Menu
                  id={`podcast-${id}-member`}
                  label="Other apps"
                  triggerClass="button button-outline"
                  items={appItems(member.url, "Copy private link", false)}
                />
              </div>
            </div>
          ) : (
            <div className="podcast-follow-row">
              <a className="button button-outline" href="/account#podcast">
                Get your private feed
              </a>
            </div>
          )}
        </div>
      )}
      {status && (
        <p className="podcast-follow-note" role="status">
          {status}
        </p>
      )}
    </div>
  );
}
